import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useParams, useSearchParams } from 'react-router-dom';
import Button from '../components/buttons/Button';
import FieldCard from '../components/cards/FieldCard';
import { generatePath, useNavigate } from 'react-router-dom';
import { ROUTE_FIELD_STATISTICS } from '../router/constants';
import { getToken } from '../utils/token';
import { COMMON_TRANSLATE_KEYS } from '../constants/translate-keys';
import { useTranslation } from 'react-i18next';
import { Field } from '../types';
import Breadcrumb from '../components/ui/Breadcrumb';
import { useComplexes } from '../hooks/useComplexes';
import ChangeListingButton from '../components/buttons/ChangeListingButton';
import { LISTING_TYPE_PARAM } from '../constants/search-params';

const initialPageLimit = 8;

const ArenaFields = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const [fields, setFields] = useState<Field[]>([]);
  const [pageLimit, setPageLimit] = useState(initialPageLimit);
  const [hasMoreFields, setHasMoreFields] = useState(false);

  let [searchParams] = useSearchParams();

  const name = searchParams.get('name');
  const listingTypeParam =
    (searchParams.get(LISTING_TYPE_PARAM) as 'list' | 'cards') || undefined;

  const { complexes } = useComplexes();

  const complex = useMemo(() => {
    return complexes.find((c) => c.id === Number(id)) || null;
  }, [complexes, id]);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const response = await axios.get(
          `${
            process.env.REACT_APP_API_ENDPOINT
          }/owner/complexes/${id}/fields?page=${1}&limit=${pageLimit}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
            params: {
              excludeExpired: true,
            },
          }
        );
        setFields(response.data.fields);
        setHasMoreFields(response.data.hasNext);
        console.log('Fields are fetched:', response.data.fields);
        console.log('Complex ID:', id);
      } catch (error) {
        console.error('Error fetching fields:', error);
      }
    };

    fetchFields();
  }, [id, pageLimit]);

  const getFieldStatisticsPath = (fieldId: number) =>
    generatePath(ROUTE_FIELD_STATISTICS, {
      id: String(id),
      fieldId: String(fieldId),
    });

  const loadMore = () => {
    if (hasMoreFields) {
      setPageLimit(pageLimit + initialPageLimit);
    }
  };

  return (
    <div className="mx-[30px] mb-10">
      <div className="flex sm:flex-row flex-col justify-between w-full pr-3 sm:items-end items-center mb-5">
        <Breadcrumb
          className="sm:mb-5 mb-10"
          links={[
            {
              to: '/my-fields',
              name: t(COMMON_TRANSLATE_KEYS.COMPLEXES),
            },
          ]}
        >
          {name} ({t(COMMON_TRANSLATE_KEYS.FIELDS)})
        </Breadcrumb>
        <ChangeListingButton listingType={listingTypeParam} />
      </div>
      <div className="flex flex-wrap gap-3">
        {(listingTypeParam === 'cards' || !listingTypeParam) &&
          fields.map((field) => (
            <FieldCard
              key={field.id}
              title={field.name}
              location={`${field.city}, ${field.country}`}
              field={field}
              complex={complex!}
              width="lg"
            >
              <div className="flex sm:flex-row flex-col gap-3 justify-center items-center w-full">
                <Button
                  backgroundColor="bg-secondaryBlack"
                  textColor="text-mainBlue"
                  border="1"
                  borderColor="border-mainBlue"
                  className="max-w-[35rem]"
                  onClick={() =>
                    navigate(`/arena/${id}/field-reservation/${field.id}`)
                  }
                >
                  {t(COMMON_TRANSLATE_KEYS.RESERVATIONS)}
                </Button>
                <Button
                  backgroundColor="bg-secondaryBlack"
                  textColor="text-mainBlue"
                  border="1"
                  borderColor="border-mainBlue"
                  className="max-w-[35rem]"
                  onClick={() => navigate(getFieldStatisticsPath(field.id))}
                >
                  {t(COMMON_TRANSLATE_KEYS.SEE_STATISTICS)}
                </Button>
              </div>
            </FieldCard>
          ))}
        {listingTypeParam === 'list' && (
          <ul className="flex flex-col gap-4 w-full">
            {fields.map((field) => (
              <li className="flex w-full justify-between gap-5 p-3 bg-additionalBlack border border-backGroundGray rounded-2xl items-center">
                <h3 className="text-mainWhite text-xl">{field.name}</h3>
                <div className="flex sm:flex-row flex-col gap-3 justify-end w-full max-w-[31rem]">
                  <Button
                    backgroundColor="bg-secondaryBlack"
                    textColor="text-mainBlue"
                    border="1"
                    borderColor="border-mainBlue"
                    className="max-w-[15rem]"
                    onClick={() =>
                      navigate(`/arena/${id}/field-reservation/${field.id}`)
                    }
                  >
                    {t(COMMON_TRANSLATE_KEYS.RESERVATIONS)}
                  </Button>
                  <Button
                    backgroundColor="bg-secondaryBlack"
                    textColor="text-mainBlue"
                    border="1"
                    borderColor="border-mainBlue"
                    className="max-w-[15rem]"
                    onClick={() => navigate(getFieldStatisticsPath(field.id))}
                  >
                    {t(COMMON_TRANSLATE_KEYS.SEE_STATISTICS)}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {fields.length === 0 && (
          <h1 className="text-lg">
            <span className="text-textGray">
              {t(COMMON_TRANSLATE_KEYS.NO_FIELDS)}
            </span>
          </h1>
        )}
      </div>
      {fields.length > 0 && hasMoreFields && (
        <Button onClick={loadMore}>{t(COMMON_TRANSLATE_KEYS.LOAD_MORE)}</Button>
      )}
    </div>
  );
};

export default ArenaFields;
